import { msg, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import CloseIcon from '@mui/icons-material/Close'
import EventIcon from '@mui/icons-material/Event'
import PlaceIcon from '@mui/icons-material/Place'
import {
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import { useMemo, useState } from 'react'
import { ConfigOptionIcon } from '../shared/config-option-icon'
import {
  buildCrashMapDetailSummary,
  canShowMapPopupAction,
  computeAgeYears,
  getCrashReportTypeLabel,
  getMapPopupPersonIconFilename,
  getPersonInjuryColor,
  getPersonRoadUserTypeLabel,
  getPersonSeatingPositionLabel,
  getRoadRowIconFilename,
  getVehicleIconFilename,
  getVehicleTypeLabel,
  isHealthcareSummaryOnlyReport,
  MAP_POPUP_ROW_KIND,
} from './crash-map-detail-utils'

const VICTIM_STYLES = {
  fatal: { color: '#D50000', labelMsg: msg`Fatal` },
  serious: { color: '#EF6C00', labelMsg: msg`Serious` },
  slight: { color: '#FFD600', labelMsg: msg`Slight` },
}

/** Fixed layout widths so person icons align across vehicle/road rows. */
const ROW_LEADING_WIDTH = 72
const PERSON_SLOT_WIDTH = 68

/**
 * Pannello dettaglio crash su mappa (compatto scuro → espanso con dettagli).
 */
export function CrashMapDetailPanel({
  crash,
  organizations = [],
  configuration,
  user,
  isMobile = false,
  isDark = false,
  onClose,
  onViewEdit,
}) {
  const { _ } = useLingui()
  const summary = useMemo(
    () => buildCrashMapDetailSummary(crash, configuration, organizations),
    [crash, configuration, organizations],
  )
  const [expanded, setExpanded] = useState(false)

  const reportTypeLabel = useMemo(
    () => getCrashReportTypeLabel(crash, organizations, {
      mergedCompact: _(msg`Merged/Validated`),
      validatedExpanded: _(msg`Validated`),
      expanded,
    }),
    [crash, organizations, expanded, _],
  )

  const showAction = canShowMapPopupAction(user, crash, isMobile)
  const isValidated = Boolean(crash?.merge_date)
  const previewLabel = _(msg`INFO`)
  const actionLabel = isValidated ? _(msg`VIEW`) : _(msg`EDIT`)
  const showInvolvedParties = summary.hasInvolvedParties
    && !isHealthcareSummaryOnlyReport(crash)

  const headerBg = isDark ? '#3C4A5C' : '#4A5D6C'
  const panelBg = isDark ? '#1E293B' : '#FFFFFF'
  const textPrimary = isDark ? '#F1F5F9' : '#1E293B'
  const textMuted = isDark ? '#CBD5E1' : '#64748B'

  const actionButtonSx = {
    minWidth: 72,
    py: 0.5,
    px: 1.5,
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'none',
    bgcolor: '#E8EDF2',
    color: '#334155',
    boxShadow: 'none',
    '&:hover': { bgcolor: '#D8E0E8' },
  }

  const stopMapPointer = (event) => {
    event.stopPropagation()
  }

  const closeButtonSx = {
    flexShrink: 0,
    color: '#FFFFFF',
    width: 28,
    height: 28,
    p: 0,
    ml: 0.5,
    mt: -0.25,
    alignSelf: 'flex-start',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    '& .MuiSvgIcon-root': {
      fontSize: 18,
      display: 'block',
    },
  }

  const renderCloseButton = () => (
    <IconButton
      size="small"
      onClick={onClose}
      onPointerDown={stopMapPointer}
      onMouseDown={stopMapPointer}
      onTouchStart={stopMapPointer}
      aria-label={_(msg`Close`)}
      sx={closeButtonSx}
    >
      <CloseIcon />
    </IconButton>
  )

  const renderPersonIcon = (person, rowKind) => {
    const age = computeAgeYears(person['Date of birth'])
    const roadUserType = getPersonRoadUserTypeLabel(person, summary.configOptions)
    const seatingPosition = rowKind === MAP_POPUP_ROW_KIND.vehicle
      ? getPersonSeatingPositionLabel(person, summary.configOptions)
      : null
    const primaryLabel = rowKind === MAP_POPUP_ROW_KIND.vehicle
      ? (seatingPosition ?? roadUserType)
      : roadUserType
    const color = getPersonInjuryColor(person['Injury severity'])
    const iconFilename = getMapPopupPersonIconFilename(
      person,
      summary.configOptions,
      rowKind,
    )

    return (
      <Stack
        key={`person-${person['Person id']}-${rowKind}-${seatingPosition ?? roadUserType ?? 'unknown'}`}
        alignItems="center"
        spacing={0.25}
        sx={{ width: PERSON_SLOT_WIDTH, flexShrink: 0 }}
      >
        <Box sx={{ height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ConfigOptionIcon
            filename={iconFilename}
            size={22}
            color={color}
            alt={primaryLabel ?? ''}
          />
        </Box>
        <Stack spacing={0} alignItems="center" sx={{ width: '100%', minHeight: 24 }}>
          {primaryLabel && (
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 600,
                color: textPrimary,
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: '100%',
                textAlign: 'center',
              }}
              title={primaryLabel}
            >
              {primaryLabel}
            </Typography>
          )}
          {age != null && (
            <Typography
              sx={{
                fontSize: 10,
                color: textMuted,
                lineHeight: 1.2,
                textAlign: 'center',
                width: '100%',
              }}
            >
              {age} {_(msg`yrs`)}
            </Typography>
          )}
        </Stack>
      </Stack>
    )
  }

  const renderVictimStat = (key) => {
    const style = VICTIM_STYLES[key]
    const value = summary.victimCounts[key]
    return (
      <Box key={key} sx={{ flex: 1, textAlign: 'center', px: 0.5 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 700, color: textPrimary, lineHeight: 1.1 }}>
          {value}
        </Typography>
        <Typography sx={{ fontSize: 11, color: textMuted, mt: 0.25 }}>
          {_(style.labelMsg)}
        </Typography>
        <Box sx={{ mt: 0.75, height: 3, borderRadius: 1, bgcolor: style.color }} />
      </Box>
    )
  }

  const renderVehicleIcon = (unit) => {
    const iconFilename = getVehicleIconFilename(unit, summary.configOptions)
    return (
      <ConfigOptionIcon
        filename={iconFilename}
        size={24}
        color={textMuted}
        alt={getVehicleTypeLabel(unit, summary.configOptions) ?? ''}
      />
    )
  }

  const renderRowLeading = (row) => {
    if (row.rowKind === MAP_POPUP_ROW_KIND.road) {
      return (
        <Stack
          alignItems="center"
          spacing={0.25}
          sx={{ width: ROW_LEADING_WIDTH, flexShrink: 0 }}
        >
          <Box sx={{ height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ConfigOptionIcon filename={getRoadRowIconFilename()} size={24} color={textMuted} alt="road" />
          </Box>
          <Typography
            sx={{
              fontSize: 10,
              color: textMuted,
              width: '100%',
              textAlign: 'center',
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {_(msg`Road`)}
          </Typography>
        </Stack>
      )
    }

    const vehicleLabel = getVehicleTypeLabel(row.unit, summary.configOptions)
    if (!row.unit && !vehicleLabel) return null

    return (
      <Stack
        alignItems="center"
        spacing={0.25}
        sx={{ width: ROW_LEADING_WIDTH, flexShrink: 0 }}
      >
        <Box sx={{ height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {renderVehicleIcon(row.unit)}
        </Box>
        {vehicleLabel && (
          <Typography
            sx={{
              fontSize: 10,
              color: textMuted,
              width: '100%',
              textAlign: 'center',
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
            title={vehicleLabel}
          >
            {vehicleLabel}
          </Typography>
        )}
      </Stack>
    )
  }

  const renderSpeedLimitBadge = () => {
    if (!summary.speedLimit) return null
    return (
      <Box
        sx={{
          ml: summary.hasVictimStats ? 0.5 : 'auto',
          width: 44,
          height: 44,
          borderRadius: '50%',
          border: '3px solid #D50000',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          bgcolor: '#FFFFFF',
        }}
      >
        <Typography sx={{ fontSize: 15, fontWeight: 800, color: textPrimary }}>
          {summary.speedLimit}
        </Typography>
      </Box>
    )
  }

  const renderExpandedBody = () => (
    <Box sx={{ px: 1.5, pb: 1.5, pt: 1, bgcolor: panelBg }}>
      {summary.dateTime && (
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
          <EventIcon sx={{ fontSize: 16, color: textMuted }} />
          <Typography sx={{ fontSize: 13, color: textPrimary }}>
            {summary.dateTime}
          </Typography>
        </Stack>
      )}
      {summary.location && (
        <Stack direction="row" alignItems="flex-start" spacing={0.5} sx={{ mb: 1.25 }}>
          <PlaceIcon sx={{ fontSize: 16, color: textMuted, mt: '1px' }} />
          <Typography sx={{ fontSize: 12, color: textMuted }}>
            {summary.location}
          </Typography>
        </Stack>
      )}

      {(summary.hasVictimStats || summary.speedLimit) && (
        <Stack direction="row" alignItems="flex-end" spacing={0.5} sx={{ mb: 1.25 }}>
          {summary.hasVictimStats && (
            <>
              {renderVictimStat('fatal')}
              {renderVictimStat('serious')}
              {renderVictimStat('slight')}
            </>
          )}
          {renderSpeedLimitBadge()}
        </Stack>
      )}

      {showInvolvedParties && (
        <Box
          sx={{
            maxHeight: 160,
            overflowY: 'auto',
            borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : '#E2E8F0'}`,
            pt: 1,
          }}
        >
          {summary.unitPeopleRows.map((row, index) => (
              <Stack
                key={`unit-row-${row.rowKind}-${index}`}
                direction="row"
                alignItems="flex-start"
                spacing={1}
                sx={{
                  py: 0.375,
                  borderBottom:
                    index < summary.unitPeopleRows.length - 1
                      ? `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#F1F5F9'}`
                      : 'none',
                }}
              >
                {renderRowLeading(row)}
                <Stack
                  direction="row"
                  spacing={0.5}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ flex: 1, minWidth: 0, pt: '1px' }}
                >
                  {row.people.map((person) => renderPersonIcon(person, row.rowKind))}
                </Stack>
              </Stack>
            ))}
        </Box>
      )}

      {!summary.hasVictimStats && !summary.speedLimit && !showInvolvedParties && !summary.location && (
        <Typography sx={{ fontSize: 12, color: textMuted, fontStyle: 'italic', py: 1 }}>
          <Trans>Additional details will appear when available.</Trans>
        </Typography>
      )}
    </Box>
  )

  const renderCompactPanel = () => (
    <Box
      sx={{
        width: 240,
        maxWidth: '92vw',
        borderRadius: 1.5,
        overflow: 'hidden',
        boxShadow: '0 8px 24px rgba(0,0,0,0.22)',
        bgcolor: headerBg,
        color: '#FFFFFF',
        px: 1.5,
        py: 1.25,
        textAlign: 'center',
      }}
      onPointerDown={stopMapPointer}
      onMouseDown={stopMapPointer}
      onTouchStart={stopMapPointer}
    >
      <Stack direction="row" alignItems="flex-start" spacing={0.5} sx={{ mb: 0.5 }}>
        <Typography
          sx={{
            flex: 1,
            fontSize: 11,
            opacity: 0.85,
            lineHeight: 1.2,
            textAlign: 'left',
            minWidth: 0,
          }}
        >
          <Trans>Report type</Trans>: {reportTypeLabel}
        </Typography>
        {renderCloseButton()}
      </Stack>
      <Typography sx={{ fontSize: 12, opacity: 0.85, mt: 1.25, mb: 0.25 }}>
        <Trans>Crash id</Trans>:
      </Typography>
      <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1.25 }}>
        {crash?.['Crash id']}
      </Typography>
      <Button
        size="small"
        variant="contained"
        onClick={() => setExpanded(true)}
        sx={{
          ...actionButtonSx,
          width: '100%',
        }}
      >
        {previewLabel}
      </Button>
    </Box>
  )

  if (!expanded) {
    return renderCompactPanel()
  }

  return (
    <Box
      sx={{
        width: 360,
        maxWidth: '92vw',
        borderRadius: 1.5,
        overflow: 'hidden',
        boxShadow: '0 8px 24px rgba(0,0,0,0.22)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.25,
          py: 1,
          bgcolor: headerBg,
          color: '#FFFFFF',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 11, opacity: 0.85, lineHeight: 1.2 }}>
            <Trans>Report type</Trans>
          </Typography>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              lineHeight: 1.25,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {reportTypeLabel}
          </Typography>
        </Box>
        {showAction && (
          <Button
            size="small"
            variant="contained"
            onClick={() => onViewEdit?.(crash)}
            sx={actionButtonSx}
          >
            {actionLabel}
          </Button>
        )}
        {renderCloseButton()}
      </Box>

      {renderExpandedBody()}
    </Box>
  )
}
